/**
 * SOP Recorder save path: mint signed PUT URLs → PUT every file straight to
 * GCS → finalize. Bytes never transit Cloud Run, whose 32 MiB request cap a
 * long narrated session blows through. The per-recording ``UploadState`` lets
 * a retry skip files that already landed (and re-mint once the URLs expire).
 *
 * Also: ``zipRecording`` — the local "Download Recording" fallback so a save
 * that fails for any reason never costs the user the session.
 */

import { addSeconds, format, isAfter } from "date-fns";
import { strToU8, Zip, ZipPassThrough } from "fflate";

import { fetchApi } from "@/lib/api";
import type { SopCapturePayload } from "@/hooks/use-sop-capture";

export interface SaveRecordingResult {
  id: string;
  title: string | null;
  status: string;
  duration_seconds: number;
  screenshot_count: number;
  transcript: string | null;
}

interface MintResponse {
  recording_id: string;
  upload_token: string;
  audio_url: string;
  screenshot_urls: string[];
  expires_in_seconds: number;
}

/** Survives across retries of ONE recording. */
export interface UploadState {
  title: string;
  mint?: MintResponse;
  mintedAt?: Date;
  /** File indices already PUT (0 = audio, 1.. = screenshots). */
  done: Set<number>;
}

export interface UploadProgress {
  done: number;
  total: number;
}

const PUT_CONCURRENCY = 4;
// Re-mint this long before the URLs actually expire, so a PUT never races it.
const MINT_EXPIRY_MARGIN_S = 300;

export function newUploadState(): UploadState {
  return {
    title: `Recording · ${new Date().toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`,
    done: new Set(),
  };
}

function audioExtension(mime: string): string {
  return mime.includes("webm") ? "webm" : "mp4";
}

function files(payload: SopCapturePayload): { blob: Blob; type: string }[] {
  return [
    { blob: payload.audio, type: payload.audioMimeType },
    ...payload.screenshots.map((s) => ({ blob: s.blob, type: s.blob.type || "image/png" })),
  ];
}

function mintIsFresh(state: UploadState): boolean {
  if (!state.mint || !state.mintedAt) return false;
  const staleAt = addSeconds(state.mintedAt, state.mint.expires_in_seconds - MINT_EXPIRY_MARGIN_S);
  return !isAfter(new Date(), staleAt);
}

/** Whether resending can succeed. A 4xx is deterministic EXCEPT timeout,
 *  rate-limit, and 409 (finalize found a file missing — re-PUT fixes it). */
export function isRetryableSaveError(err: unknown): boolean {
  const status = (err as { status?: unknown }).status;
  return !(typeof status === "number" && status >= 400 && status < 500 && ![408, 409, 429].includes(status));
}

export async function saveRecording(
  payload: SopCapturePayload,
  state: UploadState,
  onProgress: (p: UploadProgress) => void,
): Promise<SaveRecordingResult> {
  const all = files(payload);

  // Every file already landed means a finalize may already have committed
  // (its response lost). Finalize with THAT token, even past expiry — the
  // server returns the saved row — instead of re-minting a new recording id
  // and saving a duplicate. A 409 from it clears the mint for the next try.
  const allUploaded = state.mint !== undefined && state.done.size === all.length;
  const reusingStaleMint = allUploaded && !mintIsFresh(state);
  if (!allUploaded && !mintIsFresh(state)) {
    state.mint = (await fetchApi("/sop-recorder/recordings/upload-urls", {
      method: "POST",
      body: JSON.stringify({
        audio: { content_type: payload.audioMimeType, size_bytes: payload.audio.size },
        screenshots: all.slice(1).map((f) => ({ content_type: f.type, size_bytes: f.blob.size })),
      }),
    })) as MintResponse;
    state.mintedAt = new Date();
    // Fresh keys: nothing has been PUT to them yet.
    state.done.clear();
  }
  const mint = state.mint as MintResponse;
  const urls = [mint.audio_url, ...mint.screenshot_urls];
  onProgress({ done: state.done.size, total: all.length });

  // Raw PUTs straight to GCS — no fetchApi (no auth header, no base URL).
  // Content-Type is sent explicitly: finalize allowlists the STORED type.
  const queue = all.map((_, i) => i).filter((i) => !state.done.has(i));
  // Stop draining on the first failure, and only credit a PUT to `done` while
  // its mint is still current: Promise.all rejects on the first failure but
  // sibling PUTs already in flight still settle — after a re-mint, crediting
  // one would mark a file "uploaded" that only reached the OLD keys.
  let failed = false;
  const putNext = async (): Promise<void> => {
    for (let i = queue.shift(); i !== undefined && !failed; i = queue.shift()) {
      const { blob, type } = all[i];
      const res = await fetch(urls[i], { method: "PUT", body: blob, headers: { "Content-Type": type } });
      if (!res.ok) {
        failed = true;
        // 400/403 from GCS = expired/invalid signature — re-mint on the retry.
        // (No `.status` on the error: a storage failure is always retryable.)
        if (res.status === 400 || res.status === 403) state.mint = undefined;
        throw new Error(`Upload to storage failed (${res.status})`);
      }
      if (state.mint !== mint) return;
      state.done.add(i);
      onProgress({ done: state.done.size, total: all.length });
    }
  };
  const workers = Array.from({ length: Math.min(PUT_CONCURRENCY, queue.length) }, putNext).map((w) =>
    w.catch((err: unknown) => {
      failed = true;
      throw err;
    }),
  );
  await Promise.all(workers);

  // Array order IS the SOP step order (the user can reorder steps before
  // stopping); offsets and click points ride along as capture metadata.
  try {
    return (await fetchApi("/sop-recorder/recordings/finalize", {
      method: "POST",
      body: JSON.stringify({
        upload_token: mint.upload_token,
        duration_seconds: payload.durationSeconds,
        screenshot_offsets_ms: payload.screenshots.map((s) => Math.round(s.offsetMs)),
        screenshot_clicks: payload.screenshots.map((s) => s.click ?? null),
        title: state.title,
      }),
    })) as SaveRecordingResult;
  } catch (err) {
    // 409 = a file isn't in storage after all, or the upload link expired —
    // re-mint and re-PUT everything on retry.
    if ((err as { status?: unknown }).status === 409) {
      state.mint = undefined;
      state.done.clear();
      // The stale-token probe above found no saved row: go straight on to a
      // fresh upload rather than making the user click Retry twice. (Once:
      // the next pass mints fresh, so `reusingStaleMint` is false.)
      if (reusingStaleMint) return saveRecording(payload, state, onProgress);
    }
    throw err;
  }
}

/** One .zip of everything captured — the never-lose-it fallback. Streamed a
 *  file at a time into Blob parts (stored, not deflated: Opus audio and PNGs
 *  are already compressed), so a long session never needs one giant
 *  contiguous buffer, and the awaits between files keep the tab responsive. */
export async function zipRecording(payload: SopCapturePayload): Promise<{ filename: string; blob: Blob }> {
  const parts: Uint8Array[] = [];
  let zipError: Error | null = null;
  const zip = new Zip((err, chunk) => {
    if (err) zipError = err;
    else parts.push(chunk);
  });
  const add = (name: string, data: Uint8Array) => {
    const entry = new ZipPassThrough(name);
    zip.add(entry);
    entry.push(data, true);
  };

  add(`narration.${audioExtension(payload.audioMimeType)}`, new Uint8Array(await payload.audio.arrayBuffer()));
  for (const [i, s] of payload.screenshots.entries()) {
    add(`shot-${String(i).padStart(3, "0")}.png`, new Uint8Array(await s.blob.arrayBuffer()));
  }
  add(
    "steps.json",
    strToU8(
      JSON.stringify(
        {
          duration_seconds: payload.durationSeconds,
          steps: payload.screenshots.map((s, i) => ({
            file: `shot-${String(i).padStart(3, "0")}.png`,
            offset_ms: Math.round(s.offsetMs),
            click: s.click ?? null,
          })),
        },
        null,
        2,
      ),
    ),
  );
  zip.end();
  if (zipError) throw zipError;
  return {
    filename: `sop-recording-${format(new Date(), "yyyy-MM-dd-HHmm")}.zip`,
    blob: new Blob(parts as BlobPart[], { type: "application/zip" }),
  };
}
